import { useEffect, useMemo, useState } from 'react';

import {
  Archive03Icon,
  BubbleChatQuestionIcon,
  Delete02Icon,
  GitBranchIcon,
  GridViewIcon,
  LeftToRightListBulletIcon,
  MessageProgrammingIcon,
  PencilEdit01Icon,
  RestoreBinIcon,
  SlidersHorizontalIcon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FormTypeDetails, FormTypes } from 'constants/forms';
import { ContactDetailFields } from 'constants/forms';
import { MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { Permissions } from 'constants/permissions';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import AppPage from 'layouts/AppPage/AppPage';
import moment from 'moment';
import { Tab, Tabs } from 'react-bootstrap';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getFormRulesCount } from 'views/QuestionnaireDetails/Components/useQuestionnaireBuilder';

import { CreateQuestionnaireModal } from './components/CreateQuestionnaireModal/CreateQuestionnaireModal';
import Alert from 'components/Alert/Alert';
import { Button, MultipleSelect, SearchInput } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';
import { IconCountChip } from 'components/IconCountChip/IconCountChip';
import { ItemOverviewCard } from 'components/ItemOverviewCard/ItemOverviewCard';
import StatusPill from 'components/Pills/StatusPills';
import { Table } from 'components/Table/Table';
import { CustomToast } from 'components/Toast/CustomToast';

import { messagePopUp } from 'api/app-slice';
import { selectCompany } from 'api/company-slice';
import { useGetFormFromFileMutation } from 'api/forms-api';
import {
  archiveForm,
  deleteForm,
  getForms,
  restoreForm,
  saveForm,
  selectForms,
} from 'api/forms-slice';
import { getTeams, selectTeams } from 'api/teams-slice';
import { selectPermissions } from 'api/user-permissions-slice';

import styles from './Questionnaires.module.scss';

const TYPE_DOT_COLOR = {
  [FormTypes.QUESTIONNAIRE]: 'bg-blue-2',
  [FormTypes.CANDIDATE]: 'bg-green-2',
  [FormTypes.REFEREE]: 'bg-yellow-2',
};

const QuestionnairesV2 = () => {
  useDocumentTitle('Questionnaires');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const company = useSelector(selectCompany);
  const forms = useSelector(selectForms);
  const teams = useSelector(selectTeams);
  const permissions = useSelector(selectPermissions);
  const hasAccess = useAccessControl(permissions);
  const showTeamSelect = hasAccess(Permissions.TeamsRead);

  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('card'); // 'card' | 'table'
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'archived'
  const [typeFilter, setTypeFilter] = useState([]);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [teamFilter, setTeamFilter] = useState([]);
  const [formToDelete, setFormToDelete] = useState(null);
  const [createFromFile] = useGetFormFromFileMutation();

  useEffect(() => {
    Promise.all([dispatch(getTeams()), dispatch(getForms())]).then(() => setLoading(false));
  }, []);

  // Combine all relevant form categories
  const combinedForms = useMemo(() => {
    return [
      ...(forms.questionnaires || []),
      ...(forms.candidates || []),
      ...(forms.referees || []),
    ];
  }, [forms]);

  const typeFilterOptions = [
    { value: FormTypes.QUESTIONNAIRE, name: FormTypeDetails[FormTypes.QUESTIONNAIRE].label },
    { value: FormTypes.CANDIDATE, name: FormTypeDetails[FormTypes.CANDIDATE].label },
    { value: FormTypes.REFEREE, name: FormTypeDetails[FormTypes.REFEREE].label },
  ];

  const filteredForms = useMemo(() => {
    return combinedForms
      .filter(form => typeFilter.length === 0 || typeFilter.includes(form.type))
      .filter(form => !search || form.name?.toLowerCase().includes(search.toLowerCase()))
      .filter(
        form =>
          teamFilter.length === 0 ||
          (teamFilter || []).some(id =>
            id === '' || id == null
              ? form.team_id == null || form.team_id === ''
              : form.team_id === id,
          ),
      )
      .sort((a, b) => {
        const aUpdated = a.updated_at ? new Date(a.updated_at) : null;
        const bUpdated = b.updated_at ? new Date(b.updated_at) : null;
        if (aUpdated && bUpdated) return bUpdated - aUpdated;
        if (aUpdated) return -1;
        if (bUpdated) return 1;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
  }, [combinedForms, typeFilter, search, teamFilter]);

  const getTeamName = form =>
    (teams || []).find(team => team.id === form.team_id)?.name || 'Global';

  const handleDeleteForm = async form => {
    if (!form) return;
    setLoading(true);
    try {
      await dispatch(deleteForm(form));
      await dispatch(getForms());
      toast.custom(t => (
        <CustomToast
          t={t}
          type="success"
          title="Off to the bin!"
          message="Form successfully deleted"
        />
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = () => {
    const form = formToDelete;
    setFormToDelete(null);
    if (form) handleDeleteForm(form);
  };

  const handleArchiveForm = async form => {
    setLoading(true);
    try {
      await dispatch(archiveForm(form));
      await dispatch(getForms());
      dispatch(
        messagePopUp({
          text: 'Form successfully archived',
          state: MESSAGE_STATE_SUCCESS,
          hide: true,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreForm = async form => {
    setLoading(true);
    try {
      await dispatch(restoreForm(form));
      await dispatch(getForms());
      dispatch(
        messagePopUp({
          text: 'Form successfully restored',
          state: MESSAGE_STATE_SUCCESS,
          hide: true,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuestionnaire = async data => {
    setCreating(true);
    try {
      let formPayload = {
        type: data.type,
        name: data.name,
        description: data.description || '',
        ...(data.team_id != null && data.team_id !== '' ? { team_id: data.team_id } : {}),
        message: data.message || '',
        ...(data.type === FormTypes.CANDIDATE
          ? {
              fields: ContactDetailFields,
            }
          : {}),
      };
      if (data.create_from && data.file_data) {
        const questions = await createFromFile({
          from: data.create_from,
          file_data: data.file_data,
        }).unwrap();
        if (questions?.length) formPayload.fields = questions;
      }
      const { payload } = await dispatch(saveForm(formPayload));
      if (payload?.result?.form?.id) {
        setShowAddModal(false);
        navigate(`/questionnaire_details/${payload.result.form.id}`);
      }
    } finally {
      setCreating(false);
    }
  };

  const renderContent = archived => {
    const forms = filteredForms.filter(form => form.archived === archived);

    if (forms.length === 0) {
      return <p className="text-xl text-neutral-5 py-12 text-center">No questionnaires found.</p>;
    }

    if (viewMode === 'card') {
      return (
        <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {forms.map(form => (
            <ItemOverviewCard
              key={form.id}
              category={{ icon: UserGroupIcon, text: getTeamName(form) }}
              title={form.name}
              description={form.description}
              tags={[
                <StatusPill
                  status="neutral"
                  text={FormTypeDetails[form.type]?.label || form.type}
                  colorDot={TYPE_DOT_COLOR[form.type] || 'bg-neutral-4'}
                  className="px-4"
                />,
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{form.fields?.length ?? 0} question(s)</Tooltip>}>
                  <span className="inline-flex">
                    <IconCountChip
                      icon={BubbleChatQuestionIcon}
                      count={form.fields?.length ?? 0}
                      backgroundColor="bg-primary-4"
                    />
                  </span>
                </OverlayTrigger>,
                ...(form.logic_jumps?.length > 0
                  ? [
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{form.logic_jumps?.length ?? 0} logic jump(s)</Tooltip>}>
                        <span className="inline-flex">
                          <IconCountChip
                            icon={GitBranchIcon}
                            count={form.logic_jumps?.length ?? 0}
                            backgroundColor="bg-primary-4"
                          />
                        </span>
                      </OverlayTrigger>,
                    ]
                  : []),
                ...(getFormRulesCount(form.rules) > 0
                  ? [
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{getFormRulesCount(form.rules)} rule(s)</Tooltip>}>
                        <span className="inline-flex">
                          <IconCountChip
                            icon={SlidersHorizontalIcon}
                            count={getFormRulesCount(form.rules)}
                            backgroundColor="bg-primary-4"
                          />
                        </span>
                      </OverlayTrigger>,
                    ]
                  : []),
                ...(form?.message
                  ? [
                      <OverlayTrigger
                        key="message"
                        placement="top"
                        overlay={
                          <Tooltip>
                            Message configured for{' '}
                            {form?.type === FormTypes.CANDIDATE ||
                            form?.type === FormTypes.QUESTIONNAIRE
                              ? 'candidate'
                              : 'referee'}
                            : "{form?.message}"
                          </Tooltip>
                        }>
                        <span className="inline-flex">
                          <IconCountChip
                            icon={MessageProgrammingIcon}
                            count={1}
                            backgroundColor="bg-primary-4"
                          />
                        </span>
                      </OverlayTrigger>,
                    ]
                  : []),
              ]}
              subtext={`Last updated: ${moment(form?.updated_at || form?.created_at).format('DD/MM/YYYY')}`}
              onClick={() => navigate(`/questionnaire_details/${form.id}`)}
              actions={[
                {
                  label: 'Edit',
                  icon: PencilEdit01Icon,
                  onClick: () => navigate(`/questionnaire_details/${form.id}`),
                },
                ...(!archived
                  ? [
                      {
                        label: 'Archive',
                        icon: Archive03Icon,
                        onClick: () => handleArchiveForm(form),
                      },
                    ]
                  : [
                      {
                        label: 'Restore',
                        icon: RestoreBinIcon,
                        onClick: () => handleRestoreForm(form),
                      },
                    ]),
                {
                  label: 'Delete',
                  icon: Delete02Icon,
                  type: 'error',
                  onClick: () => setFormToDelete(form),
                },
              ]}
            />
          ))}
        </div>
      );
    }

    return (
      <Table.Context>
        <Table.TableContent className="overflow-x-auto">
          <Table>
            <Table.Head>
              <tr>
                <Table.Header noBorder>Name</Table.Header>
                <Table.Header noBorder>Type</Table.Header>
                <Table.Header noBorder>Team</Table.Header>
                <Table.Header noBorder>Configuration</Table.Header>
                <Table.Header noBorder>Created</Table.Header>
                <Table.Header noBorder align="right">
                  {' '}
                </Table.Header>
              </tr>
            </Table.Head>
            <Table.Body>
              {forms.map(form => (
                <Table.Row key={form.id}>
                  <Table.Col>
                    <div
                      className="cursor-pointer hover:text-primary-6 capitalize flex flex-col gap-1"
                      onClick={() => navigate(`/questionnaire_details/${form.id}`)}>
                      <span className="font-medium text-primary-4">{form.name}</span>
                      {form.description && (
                        <span className="text-lg text-neutral-6 truncate max-w-xs">
                          {form.description}
                        </span>
                      )}
                    </div>
                  </Table.Col>
                  <Table.Col>
                    <StatusPill
                      status="neutral"
                      text={FormTypeDetails[form.type]?.label || form.type}
                      colorDot={TYPE_DOT_COLOR[form.type] || 'bg-neutral-4'}
                      className="px-4"
                    />
                  </Table.Col>
                  <Table.Col>{getTeamName(form)}</Table.Col>
                  <Table.Col>
                    <div className="flex items-center gap-2">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{form.fields?.length ?? 0} question(s)</Tooltip>}>
                        <span className="inline-flex">
                          <IconCountChip
                            icon={BubbleChatQuestionIcon}
                            count={form.fields?.length ?? 0}
                            backgroundColor="bg-primary-4"
                          />
                        </span>
                      </OverlayTrigger>

                      {form.logic_jumps?.length > 0 && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>{form.logic_jumps?.length ?? 0} logic jump(s)</Tooltip>
                          }>
                          <span className="inline-flex">
                            <IconCountChip
                              icon={GitBranchIcon}
                              count={form.logic_jumps?.length ?? 0}
                              backgroundColor="bg-primary-4"
                            />
                          </span>
                        </OverlayTrigger>
                      )}
                      {getFormRulesCount(form.rules) > 0 && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{getFormRulesCount(form.rules)} rule(s)</Tooltip>}>
                          <span className="inline-flex">
                            <IconCountChip
                              icon={SlidersHorizontalIcon}
                              count={getFormRulesCount(form.rules)}
                              backgroundColor="bg-primary-4"
                            />
                          </span>
                        </OverlayTrigger>
                      )}
                      {form?.message && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>
                              Message configured for{' '}
                              {form?.type === FormTypes.CANDIDATE ||
                              form?.type === FormTypes.QUESTIONNAIRE
                                ? 'candidate'
                                : 'referee'}
                              : "{form?.message}"
                            </Tooltip>
                          }>
                          <span className="inline-flex">
                            <IconCountChip
                              icon={MessageProgrammingIcon}
                              count={1}
                              backgroundColor="bg-primary-4"
                            />
                          </span>
                        </OverlayTrigger>
                      )}
                    </div>
                  </Table.Col>
                  <Table.Col>{moment(form.created_at).format('DD/MM/YYYY')}</Table.Col>
                  <Table.Col align="right">
                    <div className="flex items-center gap-2">
                      <IconButton
                        icon={PencilEdit01Icon}
                        prefix="hugeicons"
                        tip="Edit questionnaire"
                        onClick={() => navigate(`/questionnaire_details/${form.id}`)}
                        placement="top"
                      />
                      <IconButton
                        icon={Archive03Icon}
                        prefix="hugeicons"
                        tip="Archive questionnaire"
                        onClick={() => handleArchiveForm(form)}
                        placement="top"
                      />
                      <IconButton
                        icon={Delete02Icon}
                        prefix="hugeicons"
                        tip="Delete questionnaire"
                        onClick={() => setFormToDelete(form)}
                        placement="top"
                        type="delete"
                      />
                    </div>
                  </Table.Col>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Table.TableContent>
      </Table.Context>
    );
  };

  return (
    <AppPage loading={loading} requiredPermissions={[Permissions.FormsWrite]}>
      {/* Delete confirmation modal */}
      {formToDelete && (
        <Alert
          show={!!formToDelete}
          title="Are you sure you want to delete this questionnaire permanently?"
          message="Deletion cannot be undone. Are you sure you want to continue?"
          ok="Delete"
          cancel="Cancel"
          icon={Delete02Icon}
          showConfirmationInput={true}
          type="error"
          onOk={handleConfirmDelete}
          onCancel={() => setFormToDelete(null)}
        />
      )}
      <div className={styles.root}>
        <AppPage.Header
          title={t('questionnaires.questionnaires.title')}
          subtitle={t('questionnaires.questionnaires.subtitle')}
          noBorder
        />

        {/* Filters row */}
        <div className="flex items-center gap-3 px-16 pb-12 bg-neutral-1">
          <SearchInput
            placeholder="Search questionnaires..."
            value={search}
            onChange={setSearch}
            className="w-max"
          />
          <MultipleSelect
            value={typeFilter}
            placeholder="All Types"
            onChange={setTypeFilter}
            className="w-80">
            {typeFilterOptions.map(opt => (
              <MultipleSelect.Item key={opt.value} value={opt.value}>
                {opt.name}
              </MultipleSelect.Item>
            ))}
          </MultipleSelect>
          <MultipleSelect
            value={teamFilter}
            placeholder="All Teams"
            onChange={setTeamFilter}
            className="w-80">
            {teams.map(team => (
              <MultipleSelect.Item key={team.id} value={team.id}>
                {team.name}
              </MultipleSelect.Item>
            ))}
            <MultipleSelect.Item key="global" value="">
              Global
            </MultipleSelect.Item>
          </MultipleSelect>

          {/* Spacer */}
          <div className="flex-1" />

          {/* View mode toggle */}
          <div className="flex items-center gap-1 border border-neutral-4 rounded-full p-1">
            <button
              className={cn(
                'p-2 rounded-full transition-colors',
                viewMode === 'card'
                  ? 'bg-primary-1 text-primary-6'
                  : 'text-neutral-5 hover:text-neutral-7',
              )}
              onClick={() => setViewMode('card')}>
              <HugeiconsIcon icon={GridViewIcon} size={20} strokeWidth={2} />
            </button>
            <button
              className={cn(
                'p-2 rounded-full transition-colors',
                viewMode === 'table'
                  ? 'bg-primary-1 text-primary-6'
                  : 'text-neutral-5 hover:text-neutral-7',
              )}
              onClick={() => setViewMode('table')}>
              <HugeiconsIcon icon={LeftToRightListBulletIcon} size={20} strokeWidth={2} />
            </button>
          </div>
          <Button icon="plus" small onClick={() => setShowAddModal(true)}>
            Create Questionnaire
          </Button>
        </div>

        <Tabs
          id="questionnairesV2Tabs"
          activeKey={activeTab}
          onSelect={key => setActiveTab(key)}
          className={cn('tabs pill-tabs mt-12 ml-12', styles.tabs)}>
          <Tab eventKey="active" title="Active">
            {renderContent(false)}
          </Tab>
          <Tab eventKey="archived" title="Archived">
            {renderContent(true)}
          </Tab>
        </Tabs>
      </div>
      <CreateQuestionnaireModal
        visible={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreateQuestionnaire}
        isSubmitting={creating}
        teams={teams || []}
        showTeamSelect={showTeamSelect}
      />
    </AppPage>
  );
};

export default QuestionnairesV2;
